import React, { useState, useEffect } from "react";
import { resizeParent, loadStylesheet } from '@tccshared/services/Utils';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { RRule, RRuleSet, rrulestr } from 'rrule';

import Spinner from '../Spinner';
import { Modal } from '../Modal';
import { Portal } from '../Portal';

import { useConfiguration } from '../hooks/configure';
import { useEvents } from "./events.hook";


const localizer = momentLocalizer(moment);


const stringToDate = (str) => {
    const year = str.substr(0, 4)
    const month = str.substr(4, 2)
    const date = str.substr(6, 2)
    return year + '-' + month + '-' + date;
}


const transformEvent = (item) => {
    const FORMAT = "YYYY ddd MMM DD HH:mm";

    let formatted = {};
    formatted.title = item.title;
    formatted.allDay = false;


    if (!item.meta.start_date) return null;

    // Require End Time:
    if (!item.meta.start_time) item.meta.start_time = '00:00:00';


    let start = stringToDate(item.meta.start_date);
    if (item.meta.start_time != '') start += 'T' + item.meta.start_time;

    let end;
    if (item.meta.end_date) end = stringToDate(item.meta.end_date);

    if (item.meta.end_date && item.meta.end_time) {
        end = end + 'T' + item.meta.end_time
    }
    else if (!item.meta.end_date && item.meta.end_time) {
        end = stringToDate(item.meta.start_date) + 'T' + item.meta.end_time;
    }
    else {

        // No End Date Defined
        end = moment(start).add(2, 'hours').format('YYYY-MM-DDTHH:mm:ss');
    }

    formatted.start = new Date(start);
    formatted.end = new Date(end);
    formatted.url = item.url;
    formatted.id = item.ID + Math.random();
    formatted.date = item.pretty_date.string;
    formatted.rrule = item.rrule;
    formatted.startTimestamp = moment(start).unix();
    formatted.endTimestamp = moment(end).unix();
    formatted.description = item.content;
    return formatted;
}

const transformPCOCalendarEvent = (item) => {
    const FORMAT = "YYYY ddd MMM DD HH:mm";

    let formatted = {};
    formatted.title = item.Event.name;
    formatted.allDay = (item.Instance.all_day_event) ? item.Instance.all_day_event : false;


    let start = item.starts_at;
    let end;
    if (item.ends_at) end = item.ends_at;
    else end = moment(start).add(2, 'hours').format('YYYY-MM-DDTHH:mm:ss');

    formatted.start = new Date(start);
    formatted.end = new Date(end);
    formatted.url = item.Instance.church_center_url;
    formatted.id = item.event_id + Math.random();
    formatted.date = moment(start).format('YYYY-MM-DD hh:mm A');
    // formatted.rrule = item.rrule;
    formatted.startTimestamp = moment(start).unix();
    formatted.endTimestamp = moment(end).unix();
    formatted.description = item.Instance.recurrence_description;
    return formatted;
}

const generateRecurring = (formattedEvents) => {
    let withRecurringEvents = [];

    formattedEvents.map(event => {
        const thisEvent = (event.start) ? event : transformEvent(event);

        // Add Recurring
        if (thisEvent.rrule) {


            // Get Recurring Rules
            const rule = rrulestr(event.rrule);

            // One Year Worth 
            var oneYearFromNow = new Date();
            oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

            // Create Instances
            const instances = rule.between(new Date(), oneYearFromNow);
            var i = 0;
            const eventInstances = instances.map(eventInstance => {
                i++;
                const clonedEvents = {};
                clonedEvents.title = thisEvent.title;
                clonedEvents.allDay = false;
                clonedEvents.start = moment(eventInstance).format('YYYY-MM-DTHH:mm:ss');
                clonedEvents.end = moment(eventInstance).add(2, 'hours').format('YYYY-MM-DTHH:mm:ss');
                clonedEvents.url = thisEvent.url;
                clonedEvents.id = thisEvent.id + '-' + i;
                clonedEvents.date = moment(eventInstance).format('MMMM D YYYY');
                clonedEvents.startTimestamp = moment(eventInstance).unix();
                clonedEvents.description = thisEvent.content;
                withRecurringEvents.push(clonedEvents);
            })



        }

        withRecurringEvents.push(thisEvent);
    })

    // Sort Array By Start Date
    withRecurringEvents.sort(function (a, b) { return a.startTimestamp - b.startTimestamp; });

    // Return with recurring
    return withRecurringEvents;
}

const formatDateRangeString = (date) => date.getUTCFullYear() + '-' + (date.getUTCMonth() + 1).toString().padStart(2, '0') + '-' + date.getUTCDate().toString().padStart(2, '0');

export const TCCCalendar = ({ site, display, theme, category, ics, type = null }) => {



    // Default State
    const [containerClassName, setContainerClassName] = useState('loading')
    const [calendarView, setCalendarView] = useState('month')
    const [openModal, setOpenModal] = useState(false);
    const [activeEvent, setActiveEvent] = useState(null);

    // Configure
    const { loading: configLoading, result: configResult } = useConfiguration(site);

    // Get Events
    const { loading: eventsLoading, result: eventsResult, load: loadEvents } = useEvents({ display, theme, category, ics, type }, site);

    // ----------------------------
    // Effects
    // ----------------------------

    // On Mount
    useEffect(() => {

        // Add Style Sheet
        loadStylesheet('tccCalendarCSS', `${site}/wp-content/plugins/thechurchco/components/web-components/assets/react-big-calendar.css`)

        resizeParent();

    }, [])

    useEffect(() => { resizeParent(); }, [eventsResult])

    const handleRangeChange = (range) => {
        if(type !== 'pcocalendar') return;
        const start = Array.isArray(range) ? range.at(0) : range.start;
        const end = Array.isArray(range) ? range.at(-1) : range.end;

        const args = { 
            start: formatDateRangeString(start), 
            end: formatDateRangeString(end),  
        }
        loadEvents(args);
    }


    // ----------------------------
    // Interaction
    // ----------------------------

    const onEventSelect = (item, e) => {
        if (!item.url) {
            setOpenModal(true);
            setActiveEvent(item);
            return;
        }
        const isInIframe = (window.self !== window.top) ? true : false;
        if (isInIframe) {
            window.open(item.url, '_blank').focus();
        } else {
            window.location.href = item.url;
        }
    }

    // ----------------------------
    // Render
    // ----------------------------

    const renderCalendar = () => {

        // Loader
        if (!eventsResult) return (<div className="loading"><Spinner /></div>);

        // Format
        let cleanEvents;
        let formattedEvents;
        if (type !== 'pcocalendar') {
            cleanEvents = eventsResult.map(item => transformEvent(item))
            formattedEvents = generateRecurring(cleanEvents);
        } else {
            formattedEvents = eventsResult.map(item => transformPCOCalendarEvent(item))
        }

        // Calendar
        return (
            <>
                {eventsLoading && <div className="loading"><Spinner /></div>}
                <Calendar
                    localizer={localizer}
                    events={formattedEvents}
                    startAccessor="start"
                    endAccessor="end"
                    view={calendarView}
                    views={['month', 'week', 'day']}
                    onView={(v) => setCalendarView(v)}
                    style={{ height: 1000 }}
                    onSelectEvent={onEventSelect}
                    onRangeChange={handleRangeChange}
                />
            </>
        )


    }

    const renderList = () => {
        // Loader
        if (eventsLoading === true || !eventsResult) return (<div className="loading"><Spinner /></div>);

        if (type === 'pcocalendar') return renderPCOCalendarList();

        // Format
        const filteredEvents = eventsResult.filter(item => {
            const f = (item.start) ? item : transformEvent(item);
            const r = moment(f.start).isSameOrAfter(moment())
            if (r === true) return f;
        })

        const formattedEvents = generateRecurring(filteredEvents);

        if (formattedEvents.length <= 0) return (<div className="list-container"></div>)

        // Calendar
        return (
            <div className="list-container">
                {formattedEvents.map(item => {
                    const f = (item.start) ? item : transformEvent(item);
                    return (
                        <div className="list-event" key={f.id} onClick={() => onEventSelect(f)}>
                            <b className="list-event-title">{f.title}</b>
                            <span className="list-event-date">{f.date}</span>
                        </div>
                    )
                })}
            </div>
        )
    }

    const renderPCOCalendarList = () => {
        return (
            <div className="list-container">
                {eventsResult.map(item => {
                    const f = (item.start) ? item : transformPCOCalendarEvent(item);
                    return (
                        <div className="list-event" key={f.id} onClick={() => onEventSelect(f)}>
                            <b className="list-event-title">{f.title}</b>
                            <time className="list-event-date" dateTime={moment(f.start)?.toISOString()}>{moment(f.start)?.format('LLL')}</time>
                        </div>
                    )
                })}

            </div>
        )
    }


    // Main
    var forceStyles = (display === 'feed') ? { display: 'block' } : {};

    return (
        <div className={containerClassName}>
            {display !== 'feed' && (<div className="calendar">{renderCalendar()}</div>)}
            <div className="calendar-list" style={forceStyles}>{renderList()}</div>
            <Portal selector='body' insertBefore="true">
                <Modal open={openModal} onClose={() => setOpenModal(false)} className="-centered">
                    {activeEvent && (<div className="row lightbg in-view">
                        <div className="row-defaults">
                            <h3 className="row-title">{activeEvent.title}</h3>
                            <div className="row-subtitle">{activeEvent.date}</div>
                            <div className="row-text" dangerouslySetInnerHTML={{ __html: activeEvent.description }} />
                        </div>
                    </div>)}
                </Modal>
            </Portal>
        </div>
    )


}


